import React, { useState, useMemo } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ArrowLeft,
  Pencil,
  Users,
  Building2,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Activity,
  Search,
  ChevronLeft,
  ChevronRight,
  Percent,
  Receipt,
  Eye,
} from "lucide-react";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import DrillDownBreadcrumb from "@/shared/components/admin/DrillDownBreadcrumb";
import DetailStatCard from "@/shared/components/admin/DetailStatCard";
import DetailHeaderSkeleton from "@/shared/components/skeletons/DetailHeaderSkeleton";
import DetailStatCardSkeleton from "@/shared/components/skeletons/DetailStatCardSkeleton";
import * as adminService from "@/shared/api/services/adminService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";
import * as resellerService from "@/shared/api/services/resellerService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import { RefreshCw } from "lucide-react";
import ProviderDistributionChart from "@/shared/components/dashboard/ProviderDistributionChart";
import AreaStatsChart from "@/shared/components/dashboard/TransactionChart";

const statusConfig = {
  active: {
    icon: CheckCircle2,
    color: "bg-emerald-100 text-emerald-700",
    label: "Active",
  },
  suspended: {
    icon: XCircle,
    color: "bg-red-100 text-red-700",
    label: "Suspended",
  },
  pending: {
    icon: Clock,
    color: "bg-amber-100 text-amber-700",
    label: "Pending",
  },
};

const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];

export default function AdminResellerDetail() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const resellerId = searchParams.get("id");

  const [selectedTx, setSelectedTx] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
    merchant: "all",
  });
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 10;

  // Fetch reseller details
  const {
    data: reseller,
    refetch: refetchReseller,
  } = useQuery({
    queryKey: ["reseller-detail", resellerId],
    queryFn: () => adminService.getResellerById(resellerId),
    enabled: !!resellerId,
  });



  // Fetch Merchants for this reseller
  const { data: merchants = [], isLoading: merchantsLoading } = useQuery({
    queryKey: ["reseller-merchants", resellerId],
    queryFn: () => adminService.getMerchantsByReseller(resellerId),
    enabled: !!resellerId,
  });

  // Fetch only the merchant IDs array directly from the backend (NEW METHOD)
  const { data: resellerMerchantIds = [], isLoading: idsLoading } = useQuery({
    queryKey: ["reseller-merchant-ids", resellerId],
    queryFn: () => resellerService.getMerchantsByResellerId(resellerId),
    enabled: !!resellerId,
  });

  const {
    data: paginationResult,
    isLoading: txLoading,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ["reseller-transactions", resellerId, page, pageSize, filters],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;

      const targetMerchants =
        filters.merchant !== "all" ? [filters.merchant] : resellerMerchantIds;

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        filters.search,
        backendFilters,
        targetMerchants.length > 0 ? targetMerchants : ["_none_"],
      );
    },
    enabled: !!resellerId && !merchantsLoading,
    keepPreviousData: true,
  });

  const transactions = useMemo(() => {
    return mapGlobalTransactions(paginationResult?.data || []);
  }, [paginationResult]);

  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  // Recent transactions for overview
  const { data: recentPaginationResult } = useQuery({
    queryKey: ["reseller-recent-transactions", resellerId, resellerMerchantIds],
    queryFn: () =>
      transactionService.getPaginatedTransactions(
        1,
        20,
        "",
        {},
        resellerMerchantIds.length > 0 ? resellerMerchantIds : ["_none_"],
      ),
    enabled: !!resellerId && !merchantsLoading,
  });

  const recentTransactions = useMemo(() => {
    return mapGlobalTransactions(recentPaginationResult?.data || []);
  }, [recentPaginationResult]);

  const status = statusConfig[reseller?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  // Parent engine (mock)
  const parentEngine = {
    id: reseller?.createdByEngineId || reseller?.createdByResellerId,
    name: reseller?.parentName,
    type: reseller?.parentType,
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchTransactions(),
        refetchReseller(),
        refetchStats(),
        refetchChart(),
      ]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const isSuccess = (status) =>
    ["success", "succeeded", "paid", "completed"].includes(status);

  // Chart data
  const chartStartISO = startOfDay(subDays(new Date(), 6)).toISOString();
  const chartEndISO = endOfDay(new Date()).toISOString();
  const { data: chartDataResult, refetch: refetchChart } = useQuery({
    queryKey: ["reseller-transactions-chart", resellerMerchantIds, chartStartISO, chartEndISO],
    queryFn: () => merchantService.getTransactionsChart(chartStartISO, chartEndISO, "PKR", resellerMerchantIds),
    enabled: resellerMerchantIds.length > 0,
    placeholderData: (prev) => prev,
  });
  const chartData = chartDataResult || [];

  // Reseller aggregate stats
  const {
    data: resellerStats,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ["reseller-stats", resellerMerchantIds],
    queryFn: () => adminService.getAdminResellerStats(resellerMerchantIds),
    enabled: resellerMerchantIds.length > 0,
    placeholderData: (prev) => prev,
  });

  // Provider distribution from current page
  const providerCounts = transactions
    .filter((t) => isSuccess(t.status))
    .reduce((acc, t) => {
      const key = t.providerName || t.bank_name || t.bank_code;
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

  const providerData = Object.entries(providerCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const statCards = [
    {
      title: t("todaysVolume"),
      value: `${reseller?.currency || "Rs"}${resellerStats?.todayVolume || "0.00"}`,
      icon: TrendingUp,
      color: "bg-blue-100 text-blue-600",
    },
    {
      title: t("totalVolume"),
      value: `${reseller?.currency || "Rs"}${resellerStats?.totalVolume || "0.00"}`,
      icon: Activity,
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      title: t("successRate"),
      value: `${resellerStats?.successRate || "0.00"}%`,
      icon: Percent,
      color: "bg-purple-100 text-purple-600",
    },
    {
      title: t("merchants"),
      value:
        merchants.filter((m) => m.status?.trim().toUpperCase() === "ACTIVE")
          .length || 0,
      icon: Building2,
      color: "bg-amber-100 text-amber-600",
    },
    {
      title: t("transactions"),
      value: resellerStats?.totalTransactions || 0,
      icon: Receipt,
      color: "bg-pink-100 text-pink-600",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!reseller ? (
        <DetailHeaderSkeleton />
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Link to={createPageUrl("AdminResellers")}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {reseller.name}
                  </h1>
                  <Badge className={`${status.color} gap-1 border-0`}>
                    <StatusIcon className="h-3 w-3" />
                    {t((reseller.status || "").toLowerCase()) || status.label}
                  </Badge>
                </div>
                <p className="text-slate-500">
                  {reseller.code} • {reseller.email}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing || isGenerating}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {t("refresh") || "Refresh"}
            </Button>
            <Link to={createPageUrl("AdminResellerForm") + `?id=${resellerId}`}>
              <Button className="gap-2">
                <Pencil className="h-4 w-4" />
                {t("editReseller") || "Edit Reseller"}
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Drill Down Breadcrumb */}
      {reseller && (
        <DrillDownBreadcrumb engine={parentEngine} reseller={reseller} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {!reseller || statsLoading
          ? Array.from({ length: 5 }).map((_, i) => (
            <DetailStatCardSkeleton key={i} />
          ))
          : statCards.map((stat, i) => (
            <DetailStatCard key={stat.title} {...stat} delay={i * 0.05} />
          ))}
      </div>

      <Tabs defaultValue="analytics" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="analytics" className="gap-2">
            <Activity className="h-4 w-4" />
            {t("analytics")}
          </TabsTrigger>
          <TabsTrigger value="transactions" className="gap-2">
            <Receipt className="h-4 w-4" />
            {t("allTransactions")}
          </TabsTrigger>
          <TabsTrigger value="merchants" className="gap-2">
            <Building2 className="h-4 w-4" />
            {t("merchants")}
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Volume Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2"
            >
              <AreaStatsChart
                data={chartData}
                currency={reseller?.currency || "Rs"}
                title={t("volumeTrend7Days")}
              />
            </motion.div>

            <ProviderDistributionChart data={providerData} colors={COLORS} />
            {/* Recent Transactions */}
            <div className="lg:col-span-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>
                    {t("recentActivity") || "Recent Activity"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TransactionTable
                    transactions={recentTransactions}
                    isLoading={txLoading}
                    onView={setSelectedTx}
                    mixedTypes={true}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* All Transactions Tab */}
        <TabsContent value="transactions">
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 bg-white rounded-xl p-4 border border-slate-100">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t("searchByIdOrReference")}
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setPage(0);
                  }}
                  className="pl-10"
                />
              </div>
              <Select
                value={filters.merchant}
                onValueChange={(v) => {
                  setFilters({ ...filters, merchant: v });
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Merchants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allMerchants")}</SelectItem>
                  {merchants.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.type}
                onValueChange={(v) => {
                  setFilters({ ...filters, type: v });
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTypes")}</SelectItem>
                  <SelectItem value="deposit">{t("deposits")}</SelectItem>
                  <SelectItem value="withdrawal">{t("withdrawals")}</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.status}
                onValueChange={(v) => {
                  setFilters({ ...filters, status: v });
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus")}</SelectItem>
                  <SelectItem value="pending">{t("pending")}</SelectItem>
                  <SelectItem value="processing">{t("processing")}</SelectItem>
                  <SelectItem value="success">{t("success")}</SelectItem>
                  <SelectItem value="failed">{t("failed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <TransactionTable
              transactions={transactions}
              isLoading={txLoading}
              onView={setSelectedTx}
              mixedTypes={true}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-slate-500">
                  Showing {page * pageSize + 1} to{" "}
                  {Math.min((page + 1) * pageSize, totalTransactions)} of{" "}
                  {totalTransactions.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Merchants Tab */}
        <TabsContent value="merchants">
          <Card>
            <CardHeader>
              <CardTitle>
                {t("merchantsUnderThisReseller")} ({merchants.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("merchant")}</TableHead>
                    <TableHead>{t("code")}</TableHead>
                    <TableHead>{t("status")}</TableHead>
                    <TableHead>{t("fee")}</TableHead>
                    <TableHead>{t("created")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.map((merchant) => (
                    <TableRow
                      key={merchant.id}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{merchant.name}</p>
                            <p className="text-sm text-slate-500">
                              {merchant.contact_email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{merchant.code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            statusConfig[(merchant.status || "").toLowerCase()]
                              ?.color || "bg-slate-100 text-slate-700"
                          }
                        >
                          {t((merchant.status || "").toLowerCase()) ||
                            merchant.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {merchant.fee_value}
                        {merchant.fee_type === "percentage" ? "%" : " PKR"}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {merchant.created_date
                          ? format(
                            new Date(merchant.created_date),
                            "MMM d, yyyy",
                          )
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={
                            createPageUrl("AdminMerchantDetail") +
                            `?id=${merchant.id}`
                          }
                        >
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        engine={parentEngine}
        reseller={reseller}
      />
    </div>
  );
}
